import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useSession, signOut, authClient } from "@/lib/auth";
import { api } from "@/lib/api";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Button from "@/components/ui/Button";
import TextInput from "@/components/ui/TextInput";
import PageShell from "@/components/ui/PageShell";

type Subscription = {
  id: string;
  status: string;
  billingInterval: "monthly" | "annual";
  stripePriceId: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2
      className="mt-8 mb-3 text-2xl"
      style={{ fontFamily: fonts.logo, color: colors.navy, fontWeight: 400 }}
    >
      {children}
    </h2>
  );
}

function BodyText({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        fontFamily: fonts.body,
        fontWeight: 500,
        color: `${colors.navy}bb`,
        letterSpacing: "0.03em",
      }}
    >
      {children}
    </p>
  );
}

function AccountLink({ to, children }: { to: string; children: ReactNode }) {
  const theme = usePalette();
  return (
    <Link
      to={to}
      className="underline transition-opacity hover:opacity-70"
      style={{
        fontFamily: fonts.body,
        fontWeight: 700,
        color: theme.accent,
        letterSpacing: "0.03em",
      }}
    >
      {children}
    </Link>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setDone(false);
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await authClient.changePassword({
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: true,
      });
      if (error) {
        setErr(error.message ?? "Could not change password. Check your current password.");
      } else {
        setDone(true);
        setCurrent("");
        setNext("");
        setConfirm("");
      }
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Unknown error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 grid max-w-sm gap-4">
      <TextInput
        label="Current password"
        name="current-password"
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        required
      />
      <TextInput
        label="New password"
        name="new-password"
        type="password"
        minLength={8}
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        required
      />
      <TextInput
        label="Confirm new password"
        name="confirm-password"
        type="password"
        minLength={8}
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        required
      />
      {err && (
        <p className="text-sm" style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}>
          {err}
        </p>
      )}
      {done && (
        <p className="text-sm" style={{ color: "#606b4c", fontFamily: fonts.body, fontWeight: 600 }}>
          Password updated.
        </p>
      )}
      <Button type="submit" disabled={busy}>
        {busy ? "…" : "Update password"}
      </Button>
    </form>
  );
}

export function Account() {
  const { data: session, isPending } = useSession();
  const nav = useNavigate();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      nav("/login");
      return;
    }
    api
      .get<{ subscription: Subscription | null }>("/api/account/subscription")
      .then((r) => {
        setSub(r.subscription);
      })
      .catch(() => setSub(null))
      .finally(() => setLoaded(true));
  }, [session, isPending, nav]);

  if (isPending || !session?.user)
    return (
      <PageShell title="Your account">
        <BodyText>Loading…</BodyText>
      </PageShell>
    );

  const member = sub && (sub.status === "active" || sub.status === "trialing");

  return (
    <PageShell title="Your account" eyebrow="Membership">
      <BodyText>
        Signed in as{" "}
        <strong style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}>
          {session.user.email}
        </strong>
      </BodyText>

      <SectionHeading>Membership</SectionHeading>
      {!loaded ? (
        <BodyText>Loading…</BodyText>
      ) : member ? (
        <BodyText>
          Status:{" "}
          <strong style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}>
            {sub!.status}
          </strong>{" "}
          · Plan:{" "}
          <strong style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}>
            {sub!.billingInterval}
          </strong>
          {sub!.currentPeriodEnd && (
            <>
              {" "}— renews {new Date(sub!.currentPeriodEnd).toLocaleDateString()}
              {sub!.cancelAtPeriodEnd && " (will cancel at period end)"}
            </>
          )}
        </BodyText>
      ) : (
        <BodyText>
          No active membership.{" "}
          <AccountLink to="/account/subscribe">Start your subscription →</AccountLink>
        </BodyText>
      )}

      <SectionHeading>Discord</SectionHeading>
      <BodyText>
        <AccountLink to="/account/community">Link / re-link Discord →</AccountLink>
      </BodyText>

      <SectionHeading>Billing</SectionHeading>
      <BodyText>
        <AccountLink to="/account/billing">Manage billing (Stripe Customer Portal) →</AccountLink>
      </BodyText>

      <SectionHeading>Password</SectionHeading>
      <ChangePassword />

      <div className="mt-10">
        <Button onClick={() => signOut().then(() => nav("/"))} variant="secondary">
          Sign out
        </Button>
      </div>
    </PageShell>
  );
}
